// First-time setup for owners: shop info → services → barbers → hours → open online booking.
import { useEffect, useState } from 'react';
import { useShop } from '../app/boot.tsx';
import { useShopPaths } from '../app/nav.tsx';
import { fieldErrors, message } from '../lib/errors.ts';
import { Banner, Button, Card, Field, Page, useToast } from '../ui/index.tsx';
import { toMinutes } from './useSchedule.ts';

const STEPS = ['ข้อมูลร้าน', 'บริการ', 'ช่าง', 'เวลาทำงาน', 'เปิดรับจอง'];
const DAYS: [number, string][] = [[1, 'จ.'], [2, 'อ.'], [3, 'พ.'], [4, 'พฤ.'], [5, 'ศ.'], [6, 'ส.'], [0, 'อา.']];
const RANGES_TEXT = 'เวลาต้องลงท้ายด้วย 0 หรือ 5 นาที เช่น 10:00 หรือ 10:05';

export function Setup() {
  const { api, shop } = useShop();
  const { href } = useShopPaths();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState({ name: shop.name, phone: shop.phone ?? '', address: shop.address ?? '' });
  const [services, setServices] = useState<{ id: string; name: string; durationMin: number; price: number }[]>([]);
  const [svc, setSvc] = useState({ name: '', durationMin: 30, price: '' });
  const [barbers, setBarbers] = useState<{ id: string; name: string }[]>([]);
  const [barberName, setBarberName] = useState('');
  const [days, setDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [open, setOpen] = useState('10:00');
  const [close, setClose] = useState('20:00');
  const [lunch, setLunch] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.staff.settings().then((a) => setInfo({ name: a.info.name, phone: a.info.phone ?? '', address: a.info.address ?? '' })).catch(() => {});
    // Resume after a reload (LIFF re-open, re-login): list what the shop already has instead of starting empty.
    api.staff.schedule().then((s) => {
      const merge = <T extends { id: string }>(saved: T[]) => (xs: T[]) => [...saved, ...xs.filter((x) => !saved.some((y) => y.id === x.id))];
      setServices(merge(s.services.filter((x) => x.active).map(({ id, name, durationMin, price }) => ({ id, name, durationMin, price }))));
      setBarbers(merge(s.barbers.filter((b) => b.active).map(({ id, name }) => ({ id, name }))));
    }).catch(() => {});
  }, [api]);

  async function guard(fn: () => Promise<void>) {
    setError(null);
    setErrors({});
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      const f = fieldErrors(e);
      if (Object.keys(f).length) setErrors(f);
      else setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  const saveInfo = () => guard(async () => {
    await api.staff.updateShopInfo({ name: info.name, phone: info.phone, address: info.address });
    setStep(1);
  });

  const addService = () => guard(async () => {
    const price = Number(svc.price);
    if (svc.price.trim() === '' || !Number.isInteger(price) || price < 0) { setErrors({ price: 'ราคาเป็นจำนวนเต็มบาท' }); return; }
    const { serviceId } = await api.staff.upsertService({ name: svc.name, durationMin: svc.durationMin, price, barberIds: [], active: true });
    setServices((xs) => [...xs, { id: serviceId, name: svc.name.trim(), durationMin: svc.durationMin, price }]);
    setSvc({ name: '', durationMin: 30, price: '' });
  });

  const addBarber = () => guard(async () => {
    const { barberId } = await api.staff.upsertBarber({ name: barberName, serviceIds: services.map((s) => s.id), active: true });
    setBarbers((xs) => [...xs, { id: barberId, name: barberName.trim() }]);
    setBarberName('');
  });

  const saveHours = () => guard(async () => {
    const o = toMinutes(open);
    const c = toMinutes(close, true);
    if (o === null || c === null || c <= o) { setError('เวลาปิดต้องหลังเวลาเปิด'); return; }
    if (o % 5 !== 0 || c % 5 !== 0) { setError(RANGES_TEXT); return; }
    if (lunch && (o >= 720 || c <= 780)) { setError('ช่วงพักกลางวัน 12:00–13:00 ต้องอยู่ในเวลาเปิดร้าน'); return; }
    const ranges = days.flatMap((wd) => (lunch
      ? [{ weekday: wd, startMin: o, endMin: 720 }, { weekday: wd, startMin: 780, endMin: c }]
      : [{ weekday: wd, startMin: o, endMin: c }]));
    // The shop's barbers come from the server, not this session's list, so nothing is silently skipped.
    const targets = (await api.staff.schedule()).barbers.filter((b) => b.active);
    setBarbers(targets.map(({ id, name }) => ({ id, name })));
    if (targets.length === 0) { setError('ยังไม่มีช่างในร้าน กรุณาเพิ่มช่างก่อน'); setStep(2); return; }
    try {
      for (const b of targets) await api.staff.setHours(b.id, ranges, undefined);
    } catch (e) {
      // This step has no field to show a field error on, so show it in the banner instead.
      if (fieldErrors(e).ranges) { setError(RANGES_TEXT); return; }
      throw e;
    }
    setStep(4);
  });

  const openBooking = () => guard(async () => {
    await api.staff.updateSettings({ onlineBookingOpen: true });
    setDone(true);
    toast('เปิดรับจองแล้ว', 'success');
  });

  if (done) {
    return (
      <Page title="ตั้งค่าร้าน">
        <div className="empty">
          <div className="empty-mark" aria-hidden="true">✓</div>
          <p className="empty-title">พร้อมรับจองแล้ว</p>
          <p className="empty-text">ลูกค้าจองผ่านเมนู LINE ของร้านได้ทันที ตั้งค่าเพิ่มเติมได้ที่ “จัดการร้าน”</p>
          <Button onClick={() => window.location.assign(href('/staff/today'))}>ไปหน้าคิววันนี้</Button>
        </div>
      </Page>
    );
  }

  return (
    <Page title={`ตั้งค่าร้าน · ${STEPS[step]}`}>
      <div className="steps" aria-hidden="true">{STEPS.map((s, i) => <span key={s} className={i <= step ? 'done' : ''} />)}</div>
      {error && <Banner kind="error">{error}</Banner>}

      {step === 0 && (
        <>
          <p className="muted" style={{ marginBottom: 12 }}>ยินดีต้อนรับ ✨ ตั้งค่า 5 ขั้นตอนสั้น ๆ แล้วเปิดรับจองได้เลย</p>
          <Field label="ชื่อร้าน" error={errors.name}><input value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} /></Field>
          <Field label="เบอร์โทรร้าน" error={errors.phone}><input value={info.phone} inputMode="tel" onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></Field>
          <Field label="ที่อยู่" error={errors.address}><textarea value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} /></Field>
          <Button block loading={busy} onClick={() => void saveInfo()}>ถัดไป</Button>
        </>
      )}

      {step === 1 && (
        <>
          <Card>
            <Field label="ชื่อบริการ" error={errors.name}><input value={svc.name} onChange={(e) => setSvc({ ...svc, name: e.target.value })} placeholder="เช่น ตัดผม หรือ ทาสีเจล มือ" /></Field>
            <div className="row">
              <Field label="ระยะเวลา">
                <select value={svc.durationMin} onChange={(e) => setSvc({ ...svc, durationMin: Number(e.target.value) })}>
                  {Array.from({ length: 16 }, (_, i) => (i + 1) * 15).map((d) => <option key={d} value={d}>{d} นาที</option>)}
                </select>
              </Field>
              <Field label="ราคา (บาท)" error={errors.price}><input type="number" inputMode="numeric" value={svc.price} onChange={(e) => setSvc({ ...svc, price: e.target.value })} /></Field>
            </div>
            <Button variant="secondary" block loading={busy} onClick={() => void addService()}>เพิ่มบริการ</Button>
          </Card>
          <ul className="list" style={{ listStyle: 'none', padding: 0, margin: '12px 0' }}>
            {services.map((s) => <li key={s.id} className="card">{s.name} · {s.durationMin} นาที · {s.price} บาท</li>)}
          </ul>
          <Button block disabled={services.length === 0} onClick={() => setStep(2)}>ถัดไป</Button>
        </>
      )}

      {step === 2 && (
        <>
          <Card>
            <Field label="ชื่อช่าง" error={errors.name}><input value={barberName} onChange={(e) => setBarberName(e.target.value)} placeholder="เช่น ต้น" /></Field>
            <p className="muted small" style={{ marginBottom: 8 }}>ช่างจะรับทุกบริการที่เพิ่มไว้ (แก้ไขได้ภายหลัง)</p>
            <Button variant="secondary" block loading={busy} onClick={() => void addBarber()}>เพิ่มช่าง</Button>
          </Card>
          <ul className="list" style={{ listStyle: 'none', padding: 0, margin: '12px 0' }}>
            {barbers.map((b) => <li key={b.id} className="card">ช่าง{b.name}</li>)}
          </ul>
          <Button block disabled={barbers.length === 0} onClick={() => setStep(3)}>ถัดไป</Button>
        </>
      )}

      {step === 3 && (
        <>
          <p className="section-title" style={{ marginTop: 0 }}>วันที่เปิด</p>
          <div className="filter-chips">
            {DAYS.map(([wd, label]) => (
              <button key={wd} type="button" className={days.includes(wd) ? 'active' : ''} aria-pressed={days.includes(wd)}
                onClick={() => setDays((xs) => (xs.includes(wd) ? xs.filter((x) => x !== wd) : [...xs, wd]))}>{label}</button>
            ))}
          </div>
          <div className="row">
            <Field label="เปิด"><input type="time" step={300} value={open} onChange={(e) => setOpen(e.target.value)} /></Field>
            <Field label="ปิด"><input type="time" step={300} value={close} onChange={(e) => setClose(e.target.value)} /></Field>
          </div>
          <label className="check"><input type="checkbox" checked={lunch} onChange={(e) => setLunch(e.target.checked)} /><span>พักกลางวัน 12:00–13:00</span></label>
          <p className="muted small" style={{ margin: '8px 0 12px' }}>ปรับเวลาของช่างแต่ละคนภายหลังได้ที่ “เวลาทำงาน”</p>
          <Button block loading={busy} disabled={days.length === 0} onClick={() => void saveHours()}>ใช้เวลานี้กับทุกช่าง</Button>
        </>
      )}

      {step === 4 && (
        <>
          <Card>
            <p>ตั้งค่าครบแล้ว! กดเปิดรับจอง แล้วลูกค้าจะจองผ่าน LINE ได้ทันที</p>
            <p className="muted small" style={{ marginTop: 6 }}>แนะนำ: เชื่อมกลุ่ม LINE พนักงานเพื่อรับแจ้งเตือน (จัดการร้าน → กลุ่ม LINE แจ้งเตือน)</p>
          </Card>
          <div style={{ height: 12 }} />
          <Button block loading={busy} onClick={() => void openBooking()}>เปิดรับจองออนไลน์</Button>
        </>
      )}
    </Page>
  );
}
